import { useEffect, useState, useCallback } from 'react';
import {
    Box, CircularProgress,
    Snackbar, Alert
} from '@mui/material';
import axios from 'axios';
import urls from '../../Mobx/Urls';

import Layout from '../Layout/Layout';
import ChartSelector from './ChartSelector';
import RequestsChart from './Requests/RequestsChart';
import RequestsTools from './Requests/RequestsTools';
import ActiveUsersChart from './ActiveUsers/ActiveUsersChart';
import ActiveUsersTools from './ActiveUsers/ActiveUsersTools';
import SearchesChart from './Searches/SearchesChart';
import SearchesTools from './Searches/SearchesTools';

const StatisticsPage = () => {
    // ------------- תאריכים להתחלה וסיום
    const today = new Date();
    const lastWeek = new Date();
    lastWeek.setDate(today.getDate() - 7); // 7 ימים אחורה
    const formatDate = (date) => date.toISOString().split('T')[0]; // פונקציה לעיצוב התאריך בפורמט YYYY-MM-DD
    const [startDate, setStartDate] = useState(formatDate(lastWeek));
    const [endDate, setEndDate] = useState(formatDate(today));
    // -------------
    const [activeUsersData, setActiveUsersData] = useState([]);
    const [requestsData, setRequestsData] = useState([]);
    const [searchesData, setSearchesData] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
    const [showTotalRequests, setShowTotalRequests] = useState(true);
    const [showUniqueUsers, setShowUniqueUsers] = useState(true);
    const [mode, setMode] = useState('all');
    const [email, setEmail] = useState('');
    const [selectedChart, setSelectedChart] = useState('activeUsers');

    const fetchStats = useCallback(async () => {
        try {
            setIsLoading(true);
            if (selectedChart === 'activeUsers') {
                const res = await axios.post(urls.statistics.getActiveUsers, { startDate, endDate }, { withCredentials: true });
                setActiveUsersData(res.data);
            }
            else if (selectedChart === 'requests') {
                const payload = { startDate, endDate };
                if (mode === 'user') payload.email = email;
                const res = await axios.post(urls.statistics.getRequestStats, payload, { withCredentials: true });
                setRequestsData(res.data);
            }
            // else if (selectedChart === 'searches') {
            //     const res = await axios.post(urls.statistics.getSearchStats, { startDate, endDate }, { withCredentials: true });
            //     setSearchesData(res.data);
            // }
        } catch (err) {
            console.error(err);
            setErrorMessage('שגיאה בטעינת הנתונים: ' + err?.response?.data);
            setIsSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    }, [selectedChart, startDate, endDate, mode, email]);

    useEffect(() => {
        if (selectedChart === 'requests' && mode === 'user' && !email.trim()) {
            setRequestsData([]);
            return;
        }
        fetchStats();
    }, [startDate, endDate, mode, email, selectedChart, fetchStats]);

    const renderTools = () => {
        switch (selectedChart) {
            case 'requests':
                return (
                    <RequestsTools
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        mode={mode}
                        setMode={setMode}
                        email={email}
                        setEmail={setEmail}
                        showTotalRequests={showTotalRequests}
                        setShowTotalRequests={setShowTotalRequests}
                        showUniqueUsers={showUniqueUsers}
                        setShowUniqueUsers={setShowUniqueUsers}
                        fetchStats={fetchStats}
                        setSelectedChart={setSelectedChart}
                    />
                );
            case 'activeUsers':
                return (
                    <ActiveUsersTools
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        fetchStats={fetchStats}
                        data={activeUsersData}
                    />
                );
            case 'searches':
                return (
                    <SearchesTools
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                    />
                );
            default:
                return null;
        }
    };

    const tools = [renderTools()];

    const drawerTools = [
        <ChartSelector
            key="chart-selector"
            selectedChart={selectedChart}
            setSelectedChart={setSelectedChart}
        />
    ];

    const handleCloseSnackbar = () => setIsSnackbarOpen(false);

    return (
        <Layout tools={tools} drawerTools={drawerTools} toolsConfig={{ toolsHeight: 85, toolsBackgroundColor: '#FFFFFF', drawerToolsWidth: 200 }}>
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: `calc(100vh - 150px)` }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {selectedChart === 'requests' && (
                        <RequestsChart
                            data={requestsData}
                            showTotalRequests={showTotalRequests}
                            showUniqueUsers={showUniqueUsers}
                            mode={mode}
                        />
                    )}
                    {selectedChart === 'activeUsers' && (
                        <ActiveUsersChart
                            data={activeUsersData}
                            onSelectUser={(email) => {
                                setSelectedChart('requests');
                                setMode('user');
                                setEmail(email);
                            }}
                        />
                    )}
                    {selectedChart === 'searches' && (
                        <SearchesChart
                            data={searchesData}
                        />
                    )}
                </>
            )}

            <Snackbar
                open={isSnackbarOpen}
                autoHideDuration={null}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Layout>
    );
};

export default StatisticsPage;
